import React, { useState, useMemo } from "react";
import {
  Box,
  Grid,
  Stack,
  Button,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import BedtimeIcon from "@mui/icons-material/Bedtime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WavesIcon from "@mui/icons-material/Waves";

import {
  StatCard,
  LineChart,
  DonutChart,
  VerticalBarChart,
  CombinedBarLineChart,
} from "../../../../../shared/components/charts";
import { DashboardCard } from "../../../../../shared/components/DashboardCard";
import {
  FilterDrawer,
  FilterState,
  getAppliedFilterCount,
  DurationSelector,
  DateRange,
  DashboardCardSkeleton,
  CohortGuard,
} from "../../../../../shared/components";
import {
  useSleepStats,
  useSleepTrends,
  useSleepStageDistribution,
  useSleepPatterns,
} from "../hooks";
import {
  buildFilterParams,
  transformTrendsData,
  calculateGranularity,
} from "../../shared/utils";
import { useClientScope } from "../../../../../shared/contexts";
import {
  usePersistedDateRange,
  usePersistedFilters,
  useMetricsOverallRange,
  useMetricsStats,
} from "../../../../../shared/hooks";

const SLEEP_ANALYTICS_DETAILS = {
  sleepTrends: `Displays daily patterns of sleep stages over time, illustrating how time spent in light, deep, and REM sleep fluctuates across days or weeks. These trends provide insight into changes in sleep quality, restorative depth, and overall recovery capacity.

By tracking stage distribution longitudinally, this view helps identify consistent sleep behaviours, disruptions, or improvements linked to workload, stress, or lifestyle factors. Sustained reductions in deep or REM sleep may indicate compromised recovery, while stable or improving patterns suggest effective restorative processes and balanced physiological recovery.`,
  sleepStageBreakdown: `Shows the distribution of time spent in awake, light, REM, and deep sleep stages across the workforce. This breakdown provides insight into overall sleep architecture, highlighting the balance between restorative deep sleep, cognitive-supporting REM sleep, and lighter sleep phases. Monitoring this distribution helps assess sleep quality, recovery potential, and overall restorative adequacy across the organisation.`,
  sleepPatterns: `Provides a detailed weekly overview of sleep heart rate (displayed as columns) and sleep efficiency (displayed as a line), enabling comparison of nocturnal cardiovascular activity and sleep quality across each day of the week. Sleep heart rate reflects overnight autonomic load and recovery status, while sleep efficiency represents the proportion of time in bed spent asleep, indicating overall sleep consolidation.

Together, these metrics highlight patterns such as elevated overnight heart rate during periods of stress or strain, reductions in efficiency linked to disrupted sleep, and improvements associated with adequate recovery. Tracking these trends across the week helps identify behavioural influences, workload effects, and consistency of restorative sleep.`,
};

export const SleepAnalytics: React.FC = () => {
  const theme = useTheme();
  const { effectiveClientId, isCentralAdmin, isAnonymised } = useClientScope();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { filters: filterState, setFilters: setFilterState } =
    usePersistedFilters();
  const activeFiltersCount = getAppliedFilterCount(filterState);
  const { dateRange, setDateRange } = usePersistedDateRange();
  const [sleepTrendView, setSleepTrendView] = useState("sleep-stages");

  const filterParams = useMemo(
    () => buildFilterParams(filterState, dateRange, effectiveClientId),
    [filterState, dateRange, effectiveClientId]
  );

  const granularity = useMemo(
    () => calculateGranularity(dateRange),
    [dateRange]
  );

  const overallRangeQuery = useMetricsOverallRange(effectiveClientId);

  const statsQuery = useSleepStats(filterParams);

  // Cohort count for privacy guard
  const cohortQuery = useMetricsStats({ ...filterParams, metrics: ["active_users"] });
  const activeUserCount = cohortQuery.data?.data?.active_users?.value;
  const trendsQuery = useSleepTrends({
    ...filterParams,
    metrics: [
      "sleep_efficiency",
      "sleep_debt",
      "sleep_stress",
      "hours_of_sleep",
      "sleep_stage_awake",
      "sleep_stage_light",
      "sleep_stage_rem",
      "sleep_stage_deep",
    ],
    granularity,
  });
  const distributionQuery = useSleepStageDistribution(filterParams);
  const hrPatternsQuery = useSleepPatterns({
    ...filterParams,
    metric: "sleep_hr_patterns",
    granularity: "weekly",
  });
  const efficiencyPatternsQuery = useSleepPatterns({
    ...filterParams,
    metric: "sleep_efficiency_patterns",
    granularity: "weekly",
  });

  const handleApplyFilters = (filters: FilterState) => {
    setFilterState(filters);
    setIsFilterOpen(false);
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
  };

  const trendsData = useMemo(() => {
    return transformTrendsData(trendsQuery.data, [
      "sleep_efficiency",
      "sleep_debt",
      "sleep_stress",
      "hours_of_sleep",
      "sleep_stage_awake",
      "sleep_stage_light",
      "sleep_stage_rem",
      "sleep_stage_deep",
    ]);
  }, [trendsQuery.data]);

  const sleepStagesData = useMemo(() => {
    return trendsData
      .map((item) => ({
        label: item.label,
        sleep_stage_awake: item.sleep_stage_awake ?? null,
        sleep_stage_light: item.sleep_stage_light ?? null,
        sleep_stage_rem: item.sleep_stage_rem ?? null,
        sleep_stage_deep: item.sleep_stage_deep ?? null,
      }))
      .filter(
        (item) =>
          item.sleep_stage_awake !== null ||
          item.sleep_stage_light !== null ||
          item.sleep_stage_rem !== null ||
          item.sleep_stage_deep !== null
      );
  }, [trendsData]);

  const efficiencyData = useMemo(() => {
    return trendsData
      .map((item) => ({
        label: item.label,
        sleep_efficiency: item.sleep_efficiency ?? null,
      }))
      .filter((item) => item.sleep_efficiency !== null);
  }, [trendsData]);

  const totalSleepData = useMemo(() => {
    return trendsData
      .map((item) => ({
        label: item.label,
        hours_of_sleep: item.hours_of_sleep ?? null,
      }))
      .filter((item) => item.hours_of_sleep !== null);
  }, [trendsData]);

  const sleepDebtData = useMemo(() => {
    return trendsData
      .map((item) => ({
        label: item.label,
        sleep_debt: item.sleep_debt ?? null,
      }))
      .filter((item) => item.sleep_debt !== null);
  }, [trendsData]);

  const sleepStressData = useMemo(() => {
    return trendsData
      .map((item) => ({
        label: item.label,
        sleep_stress: item.sleep_stress ?? null,
      }))
      .filter((item) => item.sleep_stress !== null);
  }, [trendsData]);

  // Extract absolute all-time min/max from the overall-range endpoint.
  // These values are scoped to the client and ignore any active date/filter selections.
  const overallMinMax = useMemo(() => {
    const metric_min = overallRangeQuery.data?.data?.metric_min ?? {};
    const metric_max = overallRangeQuery.data?.data?.metric_max ?? {};
    return {
      efficiencyMin: typeof metric_min.sleep_efficiency === "number" ? metric_min.sleep_efficiency : undefined,
      efficiencyMax: typeof metric_max.sleep_efficiency === "number" ? metric_max.sleep_efficiency : undefined,
      debtMin: typeof metric_min.sleep_debt === "number" ? metric_min.sleep_debt : undefined,
      debtMax: typeof metric_max.sleep_debt === "number" ? metric_max.sleep_debt : undefined,
      stressMin: typeof metric_min.sleep_stress === "number" ? metric_min.sleep_stress : undefined,
      stressMax: typeof metric_max.sleep_stress === "number" ? metric_max.sleep_stress : undefined,
      hrMin: typeof metric_min.resting_heart_rate === "number" ? metric_min.resting_heart_rate : undefined,
      hrMax: typeof metric_max.resting_heart_rate === "number" ? metric_max.resting_heart_rate : undefined,
      totalSleepMin: typeof metric_min.hours_of_sleep === "number" ? metric_min.hours_of_sleep : undefined,
      totalSleepMax: typeof metric_max.hours_of_sleep === "number" ? metric_max.hours_of_sleep : undefined,
    };
  }, [overallRangeQuery.data]);

  const distributionData = useMemo(() => {
    if (!distributionQuery.data?.data?.distribution) return [];
    return distributionQuery.data.data.distribution.map((item) => ({
      label: item.label,
      value: item.value,
      color:
        item.name === "deep" || item.name === "Deep"
          ? theme.palette.success.main
          : item.name === "rem" || item.name === "REM"
          ? theme.palette.warning.main
          : item.name === "light" || item.name === "Light"
          ? theme.palette.info.main
          : theme.palette.error.main,
    }));
  }, [distributionQuery.data, theme]);

  const combinedPatternsData = useMemo(() => {
    const hrPatterns = hrPatternsQuery.data?.data?.patterns || [];
    const efficiencyPatterns =
      efficiencyPatternsQuery.data?.data?.patterns || [];

    const hrMap = new Map<string, number | null>();
    hrPatterns.forEach((p) => {
      if (p.day) hrMap.set(p.day, p.value);
    });

    const efficiencyMap = new Map<string, number | null>();
    efficiencyPatterns.forEach((p) => {
      if (p.day) efficiencyMap.set(p.day, p.value);
    });

    const allDays = new Set<string>();
    hrPatterns.forEach((p) => {
      if (p.day) allDays.add(p.day);
    });
    efficiencyPatterns.forEach((p) => {
      if (p.day) allDays.add(p.day);
    });

    const dayOrder = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    return Array.from(allDays)
      .sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b))
      .map((day) => ({
        day,
        sleep_hr_patterns: hrMap.get(day) ?? null,
        sleep_efficiency_patterns: efficiencyMap.get(day) ?? null,
      }));
  }, [hrPatternsQuery.data, efficiencyPatternsQuery.data]);

  const statCards = useMemo(() => {
    if (!statsQuery.data?.data) return [];
    const stats = statsQuery.data.data;
    return [
      {
        metric: stats.sleep_performance,
        icon: BedtimeIcon,
      },
      {
        metric: stats.sleep_consistency,
        icon: CheckCircleIcon,
      },
      {
        metric: stats.sleep_efficiency,
        icon: WavesIcon,
      },
      {
        metric: stats.high_sleep_stress,
        icon: WavesIcon,
      },
    ].filter((card) => card.metric);
  }, [statsQuery.data]);

  const isLoading =
    statsQuery.isLoading ||
    trendsQuery.isLoading ||
    distributionQuery.isLoading ||
    hrPatternsQuery.isLoading ||
    efficiencyPatternsQuery.isLoading;

  const hasError =
    statsQuery.isError ||
    trendsQuery.isError ||
    distributionQuery.isError ||
    hrPatternsQuery.isError ||
    efficiencyPatternsQuery.isError;

  const renderSleepTrendChart = () => {
    if (sleepTrendView === "sleep-stages") {
      return sleepStagesData.length > 0 ? (
        <LineChart
          data={sleepStagesData}
          xAxisKey="label"
          series={[
            {
              dataKey: "sleep_stage_deep",
              label: "Slow Wave (Deep)",
              color: theme.palette.success.main,
              area: true,
              stack: "sleep",
            },
            {
              dataKey: "sleep_stage_rem",
              label: "REM",
              color: theme.palette.warning.main,
              area: true,
              stack: "sleep",
            },
            {
              dataKey: "sleep_stage_light",
              label: "Light Sleep",
              color: theme.palette.info.main,
              area: true,
              stack: "sleep",
            },
            {
              dataKey: "sleep_stage_awake",
              label: "Awake",
              color: theme.palette.error.main,
              area: true,
              stack: "sleep",
            },
          ]}
          height={300}
          yAxisMin={0}
          yAxisMax={10}
          showMinMaxLines
        />
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 300,
          }}
        >
          <Alert severity="info">No sleep stages data available</Alert>
        </Box>
      );
    }

    if (sleepTrendView === "total-sleep") {
      return totalSleepData.length > 0 ? (
        <LineChart
          data={totalSleepData}
          xAxisKey="label"
          series={[
            {
              dataKey: "hours_of_sleep",
              label: "Total Sleep (hrs)",
              color: theme.palette.primary.main,
              area: true,
            },
          ]}
          height={300}
          yAxisMin={0}
          yAxisMax={12}
          showMinMaxLines
          referenceMin={overallMinMax.totalSleepMin}
          referenceMax={overallMinMax.totalSleepMax}
        />
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 300,
          }}
        >
          <Alert severity="info">No total sleep data available</Alert>
        </Box>
      );
    }

    if (sleepTrendView === "efficiency") {
      return efficiencyData.length > 0 ? (
        <LineChart
          data={efficiencyData}
          xAxisKey="label"
          series={[
            {
              dataKey: "sleep_efficiency",
              label: "Sleep Efficiency",
              color: theme.palette.success.main,
              area: true,
            },
          ]}
          height={300}
          yAxisMin={0}
          showMinMaxLines
          referenceMin={overallMinMax.efficiencyMin}
          referenceMax={overallMinMax.efficiencyMax}
        />
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 300,
          }}
        >
          <Alert severity="info">No efficiency data available</Alert>
        </Box>
      );
    }

    if (sleepTrendView === "sleep-debt") {
      return sleepDebtData.length > 0 ? (
        <LineChart
          data={sleepDebtData}
          xAxisKey="label"
          series={[
            {
              dataKey: "sleep_debt",
              label: "Sleep Debt",
              color: theme.palette.warning.main,
              area: true,
            },
          ]}
          height={300}
          yAxisMin={0}
          showMinMaxLines
          referenceMin={overallMinMax.debtMin}
          referenceMax={overallMinMax.debtMax}
        />
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 300,
          }}
        >
          <Alert severity="info">No sleep debt data available</Alert>
        </Box>
      );
    }

    return sleepStressData.length > 0 ? (
      <LineChart
        data={sleepStressData}
        xAxisKey="label"
        series={[
          {
            dataKey: "sleep_stress",
            label: "Sleep Stress",
            color: theme.palette.error.main,
            area: true,
          },
        ]}
        height={300}
        yAxisMin={0}
        showMinMaxLines
        referenceMin={overallMinMax.stressMin}
        referenceMax={overallMinMax.stressMax}
      />
    ) : (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 300,
        }}
      >
        <Alert severity="info">No sleep stress data available</Alert>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <DurationSelector
              value={dateRange}
              onChange={handleDateRangeChange}
              buttonVariant="outlined"
            />
            <Button
              variant="text"
              size="medium"
              startIcon={<FilterListIcon />}
              color={activeFiltersCount > 0 ? "primary" : "inherit"}
              onClick={() => setIsFilterOpen(true)}
            >
              {activeFiltersCount > 0 ? `FILTER (${activeFiltersCount})` : "FILTER"}
            </Button>
          </Box>
        </Box>

        <FilterDrawer
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={handleApplyFilters}
          initialFilters={filterState}
        />

        {hasError && (
          <Alert severity="error">
            Failed to load sleep analytics data. Please try again later.
          </Alert>
        )}

        <Grid container spacing={3}>
          {isLoading ? (
            <>
              {[1, 2, 3, 4].map((index) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                  <DashboardCardSkeleton />
                </Grid>
              ))}
            </>
          ) : (
            statCards.map((stat, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                <StatCard metric={stat.metric} icon={stat.icon} />
              </Grid>
            ))
          )}
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard
              title="Sleep Trends"
              subtitle="Daily patterns of sleep stages showing fluctuations in recovery and rest quality."
              details={SLEEP_ANALYTICS_DETAILS.sleepTrends}
              titleComponent={
                <ToggleButtonGroup
                  value={sleepTrendView}
                  exclusive
                  onChange={(_, newValue) => {
                    if (newValue !== null) setSleepTrendView(newValue);
                  }}
                  size="small"
                >
                  <ToggleButton value="sleep-stages">Sleep Stages</ToggleButton>
                  <ToggleButton value="total-sleep">Total Sleep</ToggleButton>
                  <ToggleButton value="efficiency">Efficiency</ToggleButton>
                  <ToggleButton value="sleep-debt">Sleep Debt</ToggleButton>
                  <ToggleButton value="sleep-stress">Sleep Stress</ToggleButton>
                </ToggleButtonGroup>
              }
            >
              <CohortGuard
                userCount={activeUserCount}
                bypass={isCentralAdmin || !isAnonymised}
                loading={cohortQuery.isLoading}
              >
                {isLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  renderSleepTrendChart()
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <DashboardCard
              title="Sleep Stage Breakdown"
              subtitle="Distribution of time spent in Awake, Light, REM, and Deep sleep phases across the workforce."
              details={SLEEP_ANALYTICS_DETAILS.sleepStageBreakdown}
            >
              <CohortGuard
                userCount={activeUserCount}
                bypass={isCentralAdmin || !isAnonymised}
                loading={cohortQuery.isLoading}
              >
                {isLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : distributionData.length > 0 ? (
                  <DonutChart data={distributionData} height={300} />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <Alert severity="info">No distribution data available</Alert>
                  </Box>
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <DashboardCard
              title="Sleep Patterns"
              subtitle="Overview of sleep heart rate (columns) and efficiency (line) trends segmented across the week."
              details={SLEEP_ANALYTICS_DETAILS.sleepPatterns}
            >
              <CohortGuard
                userCount={activeUserCount}
                bypass={isCentralAdmin || !isAnonymised}
                loading={cohortQuery.isLoading}
              >
                {isLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : combinedPatternsData.length > 0 ? (
                <CombinedBarLineChart
                  data={combinedPatternsData}
                  xAxisKey="day"
                  barSeries={[
                    {
                      dataKey: "sleep_hr_patterns",
                      label: "Heart Rate",
                      color: theme.palette.info.main,
                    },
                  ]}
                  lineSeries={[
                    {
                      dataKey: "sleep_efficiency_patterns",
                      label: "Efficiency",
                      color: theme.palette.success.main,
                    },
                  ]}
                  height={350}
                  barYAxisMin={0}
                  lineYAxisMin={0}
                  showMinMaxLines
                  referenceBarMin={overallMinMax.hrMin}
                  referenceBarMax={overallMinMax.hrMax}
                />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <Alert severity="info">No pattern data available</Alert>
                  </Box>
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
